import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

interface UseGifShortcodeOpts {
  value: string
  onChange: (next: string) => void
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  enabled: boolean
}

interface GifShortcodeState {
  active: boolean
  query: string
}

const empty: GifShortcodeState = { active: false, query: '' }

const TRIGGER = /^\/gif(?:\s+(.*))?$/i

/**
 * Slack-style `/gif <query>` slash command for the composer.
 *
 * Triggers when the textarea contains `/gif` (or `/gif <query>`) — only at
 * the very start of the value, no other text allowed, so we don't hijack
 * messages that happen to mention GIFs.
 *
 * Returns `active=true` once triggered along with the trailing `<query>`,
 * which the parent feeds straight into the GifPicker. Picking a GIF or
 * pressing Escape clears the trigger from the textarea via `close()`.
 */
export function useGifShortcode({ value, onChange, textareaRef, enabled }: UseGifShortcodeOpts) {
  const [state, setState] = useState<GifShortcodeState>(empty)
  const valueRef = useRef(value)
  valueRef.current = value

  useEffect(() => {
    if (!enabled || !value.startsWith('/')) {
      // Skip setState when already inactive — avoids re-rendering consumers
      // on every keystroke of a normal message.
      setState(prev => prev.active ? empty : prev)
      return
    }
    const match = value.match(TRIGGER)
    if (!match) {
      setState(prev => prev.active ? empty : prev)
      return
    }
    const nextQuery = (match[1] ?? '').trim()
    setState(prev =>
      prev.active && prev.query === nextQuery
        ? prev
        : { active: true, query: nextQuery }
    )
  }, [enabled, value])

  const close = useCallback(() => {
    onChange('')
    setState(empty)
    requestAnimationFrame(() => textareaRef.current?.focus())
  }, [onChange, textareaRef])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!state.active) return false
    if (e.key === 'Escape') { e.preventDefault(); close(); return true }
    // Don't let Enter submit the literal "/gif foo" text — agents pick a
    // GIF from the popover instead. Shift+Enter still inserts a newline,
    // though typing one while /gif is active would just break the trigger.
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); return true }
    return false
  }, [state.active, close])

  return useMemo(() => ({
    active: state.active,
    query: state.query,
    close,
    handleKeyDown
  }), [state, close, handleKeyDown])
}
